import React, { useState } from "react";
import {
  Button,
  Space,
  Tag,
  Select,
  Popover,
  Form,
  Switch,
  Input,
  DatePicker,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";

const { RangePicker } = DatePicker;

/**
 * FilterForm component for adding filters
 */
const FilterForm = ({ form, filterableColumns, onFinish }) => {
  const [columnSelected, setColumnSelected] = useState(false);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ width: 300 }}
      initialValues={{
        logicalOperator: "AND",
        operator: "equalTo",
      }}
    >
      <Form.Item
        name="column"
        label="Filtra per colonna"
        rules={[{ required: true, message: "Seleziona una colonna" }]}
      >
        <Select 
          placeholder="Seleziona colonna"
          onChange={(value) => setColumnSelected(!!value)}
        >
          {filterableColumns.map((column) => (
            <Select.Option key={column.key} value={column.key}>
              {column.title}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {columnSelected && (
        <>
          <Form.Item
            name="operator"
            label="Operatore"
            rules={[{ required: true, message: "Seleziona un operatore" }]}
          >
            <Select 
              placeholder="Seleziona operatore"
              onChange={(value) => {
                // If exists operator is selected, set value to true by default
                if (value === "exists") {
                  form.setFieldsValue({ value: true });
                }
              }}
            >
              <Select.Option value="equalTo">Uguale a</Select.Option>
              <Select.Option value="notEqualTo">Non uguale a</Select.Option>
              <Select.Option value="greaterThan">Maggiore di</Select.Option>
              <Select.Option value="lessThan">Minore di</Select.Option>
              <Select.Option value="greaterThanOrEqualTo">Maggiore o uguale a</Select.Option>
              <Select.Option value="lessThanOrEqualTo">Minore o uguale a</Select.Option>
              <Select.Option value="contains">Contiene</Select.Option>
              <Select.Option value="exists">Esiste</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="logicalOperator"
            label="Operatore logico"
            rules={[{ required: true, message: "Seleziona un operatore logico" }]}
          >
            <Select>
              <Select.Option value="AND">AND</Select.Option>
              <Select.Option value="OR">OR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Valore" dependencies={["column", "operator"]}>
            {({ getFieldValue }) => {
              const columnKey = getFieldValue("column");
              const operator = getFieldValue("operator");
              const selectedColumn = filterableColumns.find(
                (c) => c.key === columnKey
              );

              // For exists operator, show a boolean switch
              if (operator === "exists") {
                return (
                  <Form.Item name="value" valuePropName="checked" noStyle>
                    <Switch
                      checkedChildren="Si"
                      unCheckedChildren="No"
                      defaultChecked={true}
                    />
                  </Form.Item>
                );
              }

              if (selectedColumn?.type === "boolean") {
                return (
                  <Form.Item name="value" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Si" unCheckedChildren="No" />
                  </Form.Item>
                );
              } else if (selectedColumn?.type === "date") {
                const currentOperator = getFieldValue("operator");
                if (currentOperator === "equalTo" || currentOperator === "notEqualTo") {
                  return (
                    <Form.Item
                      name="value"
                      rules={[{ required: true, message: "Seleziona una data" }]}
                      noStyle
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder="Seleziona data"
                      />
                    </Form.Item>
                  );
                } else {
                  return (
                    <Form.Item
                      name="value"
                      rules={[{ required: true, message: "Seleziona un intervallo" }]}
                      noStyle
                    >
                      <RangePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder={["Data inizio", "Data fine"]}
                      />
                    </Form.Item>
                  );
                }
              } else if (selectedColumn?.type === "number") {
                return (
                  <Form.Item
                    name="value"
                    rules={[
                      { required: true, message: "Inserisci un numero" },
                      { type: "number", message: "Deve essere un numero valido" }
                    ]}
                    noStyle
                  >
                    <Input 
                      type="number" 
                      placeholder="Inserisci valore numerico"
                      onChange={(e) => {
                        const value = e.target.value;
                        form.setFieldsValue({ value: value ? parseFloat(value) : undefined });
                      }}
                    />
                  </Form.Item>
                );
              } else if (selectedColumn?.options) {
                return (
                  <Form.Item
                    name="value"
                    rules={[{ required: true, message: "Inserisci un valore" }]}
                    noStyle
                  >
                    <Select placeholder="Seleziona valore">
                      {selectedColumn.options.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.text}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              } else if (selectedColumn?.type === "pointer") {
                return (
                  <Form.Item
                    name="value"
                    rules={[
                      { required: true, message: "Questo campo richiede una configurazione speciale" },
                    ]}
                    noStyle
                  >
                    <Input placeholder="ID del record" />
                  </Form.Item>
                );
              } else {
                return (
                  <Form.Item
                    name="value"
                    rules={[
                      { required: true, message: "Inserisci un valore" },
                    ]}
                    noStyle
                  >
                    <Input placeholder="Inserisci valore" />
                  </Form.Item>
                );
              }
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Aggiungi filtro
              </Button>
              <Button onClick={() => form.resetFields()}>Annulla</Button>
            </Space>
          </Form.Item>
        </>
      )}
    </Form>
  );
};

/**
 * FilterTags component to display active filters
 */
export const FilterTags = ({ filters, onRemove, onClear, noMargin }) => {
  if (!filters || filters.length === 0) return null;

  return (
    <Space wrap style={{ marginBottom: noMargin ? 0 : 16 }}>
      <span>Filtri attivi:</span>
      {filters.map((filter) => (
        <Tag
          key={filter.id}
          closable
          onClose={() => onRemove(filter.id)}
          color="blue"
        >
          {filter.columnName} {filter.operator === "equalTo" ? "=" : filter.operator === "notEqualTo" ? "≠" : filter.operator === "contains" ? "contiene" : "esiste"} {filter.displayValue}
        </Tag>
      ))}
      <Button size="small" onClick={onClear}>
        Rimuovi tutti
      </Button>
    </Space>
  );
};

/**
 * Main FiltersComponent
 */
const FiltersComponent = ({ filterableColumns, activeFilters, onFilterChange, onClearAll, hideFilterTags, onlyFilterTags }) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = (values) => {
    const selectedColumn = filterableColumns.find(
      (c) => c.key === values.column
    );

    let filterValue = values.value;
    let displayValue = values.value?.toString() || "";

    // Handle date values
    if (selectedColumn?.type === "date") {
      if (moment.isMoment(values.value)) {
        // Single date
        filterValue = values.value.toISOString();
        displayValue = values.value.format("DD/MM/YYYY");
      } else if (Array.isArray(values.value) && values.value.length === 2) {
        // Date range
        const startDate = values.value[0].startOf("day");
        const endDate = values.value[1].endOf("day");
        filterValue = {
          greaterThanOrEqualTo: startDate.toISOString(),
          lessThanOrEqualTo: endDate.toISOString(),
        };
        displayValue = `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`;
      }
    }
    
    // Handle pointer fields
    if (selectedColumn?.type === "pointer") {
      if (values.column === "defunto_pointer") {
        filterValue = { have: { nome: { matchesRegex: values.value, options: "i" } } };
        displayValue = `Nome contiene: ${values.value}`;
      } else {
        filterValue = { have: { objectId: { equalTo: values.value } } };
      }
    }
    
    // Handle number fields
    if (selectedColumn?.type === "number") {
      filterValue = parseFloat(values.value);
      // Format file size for display if it's the size_bytes field
      if (values.column === "size_bytes" && filterValue >= 1024) {
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(filterValue) / Math.log(1024));
        displayValue = `${(filterValue / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
      }
    }

    const newFilter = {
      id: Date.now(),
      column: values.column,
      columnName: selectedColumn?.title || values.column,
      value: filterValue,
      displayValue: displayValue,
      operator: values.operator,
      logicalOperator: values.logicalOperator,
    };

    onFilterChange([...activeFilters, newFilter]);
    form.resetFields();
    setPopoverVisible(false);
  };

  const handleRemoveFilter = (filterId) => {
    const newFilters = activeFilters.filter((f) => f.id !== filterId);
    onFilterChange(newFilters);
  };

  // If onlyFilterTags is true, only show the filter tags
  if (onlyFilterTags) {
    return (
      <FilterTags
        filters={activeFilters}
        onRemove={handleRemoveFilter}
        onClear={onClearAll}
      />
    );
  }

  return (
    <>
      <Popover
        content={
          <FilterForm
            form={form}
            filterableColumns={filterableColumns}
            onFinish={handleFinish}
          />
        }
        trigger="click"
        visible={popoverVisible}
        onVisibleChange={setPopoverVisible}
        placement="bottomLeft"
      >
        <Button icon={<PlusOutlined />}>Aggiungi filtro</Button>
      </Popover>

      {!hideFilterTags && (
        <FilterTags
          filters={activeFilters}
          onRemove={handleRemoveFilter}
          onClear={onClearAll}
        />
      )}
    </>
  );
};

export default FiltersComponent;